import { FormStatus } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { type JSX, useMemo } from 'react';

import ServiceNowIdentifierViewer from '@/components/ServiceNowIdentifierViewer';
import { ServiceDomainResponse } from '@/openapi/schema';
import { ServiceNowInfoFormData } from '@/pages/OAuthIntegrationInfoRequesterPage/ServiceNowInfoSection/index';
import FormSubmitButton from '@/pages/profile/apps/application/ApiProviderPage/components/FormSubmitButton';
import { SwitchContainerModeRef } from '@/types/SwitchContainerModeTypes';

import { SERVICE_NOW_INFO_FORM_ID, SEVERITY_CLASSIFICATION_OPTIONS } from './constants';

interface EditContainerProps {
  serviceDomains: ServiceDomainResponse[];
  errorMessage: string | null;
  switchContainerModeRef: SwitchContainerModeRef;
}

export default function EditContainer({
  serviceDomains,
  errorMessage,
  switchContainerModeRef,
}: EditContainerProps): JSX.Element {
  const {
    data: { serviceNowIdentifier },
  } = Form.useData<ServiceNowInfoFormData>(SERVICE_NOW_INFO_FORM_ID);
  const { setFieldStatus } = Form.useValidation(SERVICE_NOW_INFO_FORM_ID);

  const handleServiceNowIdentifierError = (message: string) => {
    setFieldStatus('/serviceNowIdentifier', {
      error: new Error(message),
    });
  };

  const serviceDomainAutocompleteOptions = useMemo(() => {
    return serviceDomains.map((serviceDomain) => ({
      content: [serviceDomain.name, serviceDomain.serviceDomainId],
      selectedKey: serviceDomain.id,
      selectedValue: serviceDomain.name,
      title: serviceDomain.name,
      value: serviceDomain.id,
    }));
  }, [serviceDomains]);

  return (
    <Form.Section.EditContainer>
      <Form.MainHeading>ServiceNow Information</Form.MainHeading>
      <Field.String
        help={{
          title: 'ServiceNow Identifier (BA/BSN)',
          content:
            'The Business application id (BAXXXXX) or Service offering id (BSNXXXXXXX) to identify the integration service in ServiceNow.',
          outset: false,
        }}
        label="ServiceNow Identifier (BA/BSN)"
        path="/serviceNowIdentifier"
        placeholder="BAXXXXX / BSNXXXXXXX"
        width="stretch"
      />
      <ServiceNowIdentifierViewer
        onError={handleServiceNowIdentifierError}
        serviceNowIdentifier={serviceNowIdentifier}
      />
      <Field.Selection
        data={serviceDomainAutocompleteOptions}
        emptyValue={null}
        help={{
          title: 'Service Domain',
          content: 'The ServiceNow service domain that this integration service belongs to.',
          outset: false,
        }}
        label="Service Domain"
        path="/serviceNowServiceDomainId"
        placeholder="Select a service domain"
        variant="autocomplete"
        width="stretch"
      />
      <Field.Selection
        help={{
          title: 'Severity classification',
          content: 'How is the severity of the data exposed by this service integration classified.',
          outset: false,
        }}
        label="Severity classification"
        path="/severityClassification"
        variant="radio"
      >
        {Object.entries(SEVERITY_CLASSIFICATION_OPTIONS).map(([value, label]) => (
          <Field.Option key={value} title={label} value={value} />
        ))}
      </Field.Selection>

      <Field.Boolean
        help={{
          title: 'Exposing personally identifiable information',
          content: 'This should be checked if the integration service exposes personal identifiable information.',
          outset: false,
        }}
        label="Exposing personally identifiable information"
        path="/piiExposed"
        variant="buttons"
      />
      <Field.Boolean
        help={{
          title: 'GDPR Compliant',
          content: 'This should be checked if the integration service is required to comply with GDPR regulations.',
          outset: false,
        }}
        label="GDPR Compliant"
        path="/gdprCompliant"
        variant="buttons"
      />
      {errorMessage && <FormStatus state="error" text={errorMessage} top="medium" />}
      <Form.Section.Toolbar>
        <FormSubmitButton switchContainerModeRef={switchContainerModeRef} />
        <Form.Section.EditContainer.CancelButton showConfirmDialog={false} />
      </Form.Section.Toolbar>
    </Form.Section.EditContainer>
  );
}
